/**
 * /alarms - tenant alarm center (DEVELOPER_CONTINUATION_GUIDE 9.5 / 18.1).
 *
 * Viewer-readable list over /v1/alarms with the operator-only ack action.
 * The default state filter is `open` because those are the rows with a
 * current operator action; clearing it exposes acknowledged/resolved history.
 */
import {
  ActionIcon,
  Badge,
  Breadcrumbs,
  Button,
  Center,
  Group,
  Loader,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconAlertTriangle,
  IconBell,
  IconCheck,
  IconRefresh,
  IconSearch,
} from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAckAlarm, useAlarms, type AlarmsParams } from "@/api/queries";
import {
  ALARM_SEVERITIES,
  ALARM_STATES,
  type Alarm,
  type AlarmSeverity,
  type AlarmState,
} from "@/api/types";
import { useAuth } from "@/auth/AuthProvider";
import { ErrorBanner } from "@/components/ErrorBanner";
import { SiteSelect } from "@/components/SiteSelect";

const PAGE_SIZE = 50;

type FiltersState = {
  site_slug: string;
  device_slug: string;
  meter_slug: string;
  severity: AlarmSeverity | "";
  state: AlarmState | "";
};

const EMPTY_FILTERS: FiltersState = {
  site_slug: "",
  device_slug: "",
  meter_slug: "",
  severity: "",
  state: "open",
};

const SEVERITY_OPTIONS = ALARM_SEVERITIES.map((severity) => ({
  value: severity,
  label: capitalize(severity),
}));

const STATE_OPTIONS = ALARM_STATES.map((state) => ({
  value: state,
  label: state === "acknowledged" ? "Acknowledged" : capitalize(state),
}));

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  dateStyle: "medium",
  timeStyle: "short",
});

export function AlarmsPage() {
  const auth = useAuth();
  const [filters, setFilters] = useState<FiltersState>(EMPTY_FILTERS);
  const [page, setPage] = useState(1);
  const [ackError, setAckError] = useState<unknown | null>(null);
  const [pendingAckId, setPendingAckId] = useState<string | null>(null);

  const canAck =
    auth.status === "authenticated" &&
    auth.profile.roles.some((role) => role === "operator" || role === "admin");

  const queryParams = useMemo<AlarmsParams>(
    () => ({
      site_slug: filters.site_slug.trim() || undefined,
      device_slug: filters.device_slug.trim() || undefined,
      meter_slug: filters.meter_slug.trim() || undefined,
      severity: filters.severity || undefined,
      state: filters.state || undefined,
      limit: PAGE_SIZE,
      offset: (page - 1) * PAGE_SIZE,
    }),
    [filters, page],
  );

  const alarmsQuery = useAlarms(queryParams);
  const ackMutation = useAckAlarm();

  const items = alarmsQuery.data?.items ?? [];
  const total = alarmsQuery.data?.page.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const setFilter = (k: keyof FiltersState, v: string) => {
    setFilters((f) => ({ ...f, [k]: v }));
    setPage(1);
  };

  const acknowledge = async (alarm: Alarm) => {
    setAckError(null);
    setPendingAckId(alarm.id);
    try {
      await ackMutation.mutateAsync(alarm.id);
    } catch (err) {
      setAckError(err);
    } finally {
      setPendingAckId(null);
    }
  };

  return (
    <Stack>
      <Breadcrumbs>
        <Text>Operations</Text>
        <Text>Alarms</Text>
      </Breadcrumbs>

      <Group justify="space-between" align="flex-start">
        <Stack gap={4}>
          <Title order={1}>Alarms</Title>
          <Text c="dimmed" size="sm">
            Tenant-scoped alarm events from worker rules. Open alarms are
            actionable; resolved rows are kept for history.
          </Text>
        </Stack>
        <Tooltip label="Refresh list">
          <ActionIcon
            variant="default"
            size="lg"
            onClick={() => alarmsQuery.refetch()}
            loading={alarmsQuery.isFetching}
            aria-label="Refresh"
          >
            <IconRefresh size={18} />
          </ActionIcon>
        </Tooltip>
      </Group>

      <Paper withBorder p="md" radius="md">
        <Group gap="sm" wrap="wrap">
          <SiteSelect
            label="Site"
            placeholder="any"
            clearable
            value={filters.site_slug || null}
            onChange={(v) => setFilter("site_slug", v ?? "")}
            w={240}
          />
          <TextInput
            label="Device slug"
            placeholder="e.g. b_stack_0_0"
            leftSection={<IconSearch size={14} />}
            value={filters.device_slug}
            onChange={(e) => setFilter("device_slug", e.currentTarget.value)}
            w={200}
          />
          <TextInput
            label="Meter slug"
            placeholder="e.g. b_stack_0_0_soc"
            leftSection={<IconSearch size={14} />}
            value={filters.meter_slug}
            onChange={(e) => setFilter("meter_slug", e.currentTarget.value)}
            w={220}
          />
          <Select
            label="Severity"
            placeholder="any"
            data={SEVERITY_OPTIONS}
            clearable
            value={filters.severity || null}
            onChange={(v) => setFilter("severity", v ?? "")}
            w={160}
          />
          <Select
            label="State"
            placeholder="any"
            data={STATE_OPTIONS}
            clearable
            value={filters.state || null}
            onChange={(v) => setFilter("state", v ?? "")}
            w={180}
          />
        </Group>
      </Paper>

      {alarmsQuery.error && <ErrorBanner error={alarmsQuery.error} />}
      {ackError !== null && <ErrorBanner error={ackError} title="Acknowledge failed" />}

      <Paper withBorder p="lg" radius="md">
        <Group justify="space-between" mb="md">
          <Group gap="xs">
            <IconBell size={20} />
            <Title order={3}>Results</Title>
          </Group>
          <Text c="dimmed" size="sm">
            {total} alarm{total === 1 ? "" : "s"}
          </Text>
        </Group>

        {alarmsQuery.isLoading ? (
          <Center py="xl">
            <Loader />
          </Center>
        ) : items.length === 0 ? (
          <Text c="dimmed">No alarms match the current filters.</Text>
        ) : (
          <Table highlightOnHover verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Alarm</Table.Th>
                <Table.Th>Site / Device</Table.Th>
                <Table.Th>Severity</Table.Th>
                <Table.Th>State</Table.Th>
                <Table.Th>Opened</Table.Th>
                <Table.Th>Acknowledged</Table.Th>
                <Table.Th style={{ textAlign: "right" }}>Actions</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {items.map((alarm) => (
                <Table.Tr key={alarm.id}>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text fw={500}>{alarm.message}</Text>
                      <Text c="dimmed" size="xs">
                        {alarm.code}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm">
                        <Link to={`/sites/${alarm.site_slug}`}>{alarm.site_slug}</Link>
                      </Text>
                      <Text c="dimmed" size="xs">
                        {alarm.device_slug ? (
                          <Link
                            to={`/sites/${alarm.site_slug}/devices/${alarm.device_slug}`}
                          >
                            {alarm.device_slug}
                          </Link>
                        ) : (
                          "(site)"
                        )}
                        {alarm.meter_slug ? ` / ${alarm.meter_slug}` : ""}
                      </Text>
                    </Stack>
                  </Table.Td>
                  <Table.Td>
                    <Badge
                      variant="light"
                      color={severityColor(alarm.severity)}
                      leftSection={
                        alarm.severity === "critical" ? (
                          <IconAlertTriangle size={12} />
                        ) : undefined
                      }
                    >
                      {alarm.severity}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Badge variant="light" color={stateColor(alarm.state)}>
                      {alarm.state}
                    </Badge>
                  </Table.Td>
                  <Table.Td>
                    <Text size="sm">{formatDate(alarm.opened_at)}</Text>
                  </Table.Td>
                  <Table.Td>
                    <Stack gap={0}>
                      <Text size="sm">{formatDate(alarm.acknowledged_at)}</Text>
                      {alarm.acknowledged_by && (
                        <Text c="dimmed" size="xs">
                          {alarm.acknowledged_by}
                        </Text>
                      )}
                    </Stack>
                  </Table.Td>
                  <Table.Td style={{ textAlign: "right" }}>
                    {alarm.state === "open" ? (
                      <Tooltip
                        label={
                          canAck
                            ? "Acknowledge alarm"
                            : "Operator or admin role required"
                        }
                      >
                        <Button
                          size="xs"
                          variant="light"
                          leftSection={<IconCheck size={14} />}
                          disabled={!canAck}
                          loading={pendingAckId === alarm.id}
                          onClick={() => void acknowledge(alarm)}
                          aria-label={`Acknowledge ${alarm.message}`}
                        >
                          Ack
                        </Button>
                      </Tooltip>
                    ) : (
                      <Text c="dimmed" size="sm">
                        -
                      </Text>
                    )}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}

        {totalPages > 1 && (
          <Group justify="center" mt="md">
            <Pagination value={page} onChange={setPage} total={totalPages} />
          </Group>
        )}
      </Paper>
    </Stack>
  );
}

function capitalize(value: string): string {
  return value.slice(0, 1).toUpperCase() + value.slice(1);
}

function formatDate(value: string | null): string {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return DATE_TIME.format(date);
}

function severityColor(severity: AlarmSeverity): string {
  if (severity === "critical") return "red";
  if (severity === "warning") return "orange";
  return "blue";
}

function stateColor(state: AlarmState): string {
  if (state === "open") return "red";
  if (state === "acknowledged") return "yellow";
  return "green";
}
